import { Box, styled } from '@mui/material';
import { fontWeight } from '../../theme/typography';
import { palette } from '../../theme/palette';

export const RootContainer = styled(Box)`
  position: relative;
  width: 100%;
`;

export const IconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 100%;
  color: ${palette.primary.main};
  z-index: 1;
`;

export const StartIconContainer = styled(IconContainer)`
  position: absolute;
  top: 0;
  left: 0;
`;

export const EndIconContainer = styled(IconContainer, {
  shouldForwardProp: (prop) => prop !== 'isClickable',
})<{
  isClickable?: boolean;
}>`
  position: absolute;
  top: 0;
  right: 0;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'auto')};
`;

export const MainContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'paddingRightValue' && prop !== 'paddingLeftValue',
})<{
  paddingRightValue?: string | null;
  paddingLeftValue?: string | null;
}>`
  display: flex;
  flex: 1;
  position: relative;
  height: 40px;
  width: 100%;
  background: ${(props) => (props['aria-disabled'] ? '#e4e8ed80' : 'white')};
  padding-left: ${(props) => (props.paddingLeftValue ? props.paddingLeftValue : '40px')};
  padding-right: ${(props) => (props.paddingRightValue ? props.paddingRightValue : '0px')};
  box-sizing: border-box;
`;

export const StyledFieldContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'clearButtonVisible',
})<{
  clearButtonVisible?: boolean | null;
}>`
  width: ${(props) => (props.clearButtonVisible ? 'calc(100% - 128px)' : '100%')};
  display: flex;
  align-items: center;
`;

export const ClearContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 10px;
  position: absolute;
  right: 0;
  background: none;
  box-sizing: border-box;
  width: 128px;
`;

export const ClearButton = styled(Box)`
  color: ${palette.primary.main};
  background: white;
  border: none;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  :hover {
    background: white;
  }
`;

export const StyledTypography = styled(Box)`
  font-size: 14px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  letter-spacing: 0.35px;
  width: 100%;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  padding: 0 10px 0 15px;
`;

export const StyledLi = styled('li')`
  padding: 0;
  list-style: none;
  margin: 0;
`;

export const GroupHeader = styled('div')(() => ({
  padding: '2px 2px',
  color: palette.primary.main,
  textTransform: 'uppercase',
  fontStyle: 'normal',
  fontWeight: fontWeight.heavy,
  fontFamily: 'trade-gothic-next-condensed, sans-serif',
  fontSize: '12px',
  lineHeight: '28px',
  letterSpacing: '0.3px',
}));

export const GroupItem = styled('ul', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{
  isActive?: boolean;
}>(({ isActive }) => ({
  marginLeft: '2px !important',
  padding: isActive ? '2px 0px 2px 10px' : '2px 10px 2px 0px',
  background: isActive ? palette.custom.lightGrey : 'none',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: fontWeight.regular,
  lineHeight: '28px',
  letterSpacing: '0.35px',
  margin: 0,
  cursor: 'pointer',
  transition: 'padding 0.4s',
  borderRadius: '5px',
}));

export const StyledInput = styled('input', {
  shouldForwardProp: (prop) => prop !== 'textOverflow',
})<{
  textOverflow?: string | null;
}>`
  font-family: trade-gothic-next, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  letter-spacing: 0.35px;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 0 10px 0 15px;
  border: 0;
  display: block;
  overflow: hidden;
  text-overflow: ${(props) => (props.textOverflow ? props.textOverflow : 'ellipsis')};
  white-space: nowrap;
  background: ${(props) => (props.disabled ? '#e4e8ed80' : 'none')};
  :focus-visible {
    outline: 0;
  }
  ::placeholder {
    font-size: 14px;
    font-style: normal;
    font-weight: ${fontWeight.regular};
    letter-spacing: 0.35px;
    color: #757575;
  }
`;

export const StyledOptions = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'position' && prop !== 'positionOffset' && prop !== 'paddingTopAndBottom',
})<{
  position?: string;
  positionOffset?: string;
  paddingTopAndBottom?: boolean;
}>`
  position: ${(props) => props.position ?? 'absolute'};
  background: ${(props) => (props.position === 'absolute' ? 'white' : 'none')};
  z-index: 1;
  width: calc(100%);
  left: 0px;
  top: ${(props) => (props.position === 'absolute' ? `${props.positionOffset}` : '0px')};
  padding: ${(props) =>
    props.paddingTopAndBottom ? '15px 0px' : props.position === 'absolute' ? '15px' : '0px'};
  box-shadow: ${(props) => (props.position === 'absolute' ? '0px 8px 15px 0px #0000001a' : 'none')};
  box-sizing: border-box;
  border-radius: 5px;
`;

export const OptionItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'paddingRequired',
})<{
  paddingRequired?: boolean;
}>`
  padding: ${({ paddingRequired }) => (paddingRequired ? '0px 15px' : '0px')};
`;

export const HelperTextContainer = styled(Box)`
  border-top: 0.5px solid ${palette.custom.grey};
  margin-top: 19px;
`;
